/**
 * Kojiru Honest Status — single source of truth on the frontend.
 *
 * Mirrors `/app/memory/MARKETING_FACTS.md` §1 (deployed contracts) and §3
 * (audit / formal-verification scope). A backend Pytest regression
 * (`/app/backend/tests/test_honest_status_regression.py`) parses
 * MARKETING_FACTS.md and asserts the counts below match the source.
 *
 * If you change either file, run that test to confirm both still agree.
 *
 * Last sync: 2026-06-28 (Credibility Sweep V3).
 */

// Mainnet deployed contracts (MARKETING_FACTS §1.1–§1.6, §1.13, §1.14).
// Each contract has been on-chain verified (`eth_getCode` or BaseScan source-verified)
// at the time of last sync. `status` reflects honest observed activity, not aspirational copy.
export const MAINNET_CONTRACTS = [
  { key: 'koji',           name: 'KOJI',                address: '0xb50007B292256E2e54253D2110013465f9441482', status: 'active',    role: 'ERC-20 token; 10K+ transfers' },
  { key: 'staking',        name: 'KojiruStakingV2',     address: '0xaae9ae4d52fe13384522e0ef4114b0a5f5d62d68', status: 'active',    role: 'Staking; user stakes live' },
  { key: 'volReserve',     name: 'VolatilityReserve',   address: '',                                          status: 'dormant',   role: 'Safety module; no user txns' },
  { key: 'burnToBoost',    name: 'BurnToBoost',         address: '',                                          status: 'dormant',   role: 'Tokenomics module; no user txns' },
  { key: 'exitStability',  name: 'ExitStabilityFee',    address: '',                                          status: 'dormant',   role: 'Tokenomics module; no user txns' },
  { key: 'harvestVault',   name: 'HarvestVault',        address: '0xB6a79B470D480A436aC59837Ec07F36FafE0c4C5', status: 'dormant',   role: 'Revenue distribution; no deposits yet' },
  { key: 'withdrawalGuard',name: 'WithdrawalGuard',     address: '0xE3D4B3BA88f53C2D2a499cb46Cd4679dE6c2021E', status: 'enforcing', role: '10%/24h rate-limit; 0 epochs elapsed (pool not user-active)' },
  { key: 'guardianPause',  name: 'GuardianPause',       address: '0x74642654bFf0AB1F22d8747396cb1eC217f04Db3', status: 'standby',   role: 'Protocol-wide emergency-pause circuit breaker' },
];

// Aggregate counts derived from the table above. Used by the homepage widget.
// CHANGE WITH CARE — regression test asserts these match real array contents.
export const COUNTS = {
  total: MAINNET_CONTRACTS.length,                                                                // 8
  active: MAINNET_CONTRACTS.filter(c => c.status === 'active').length,                            // 2
  enforcing: MAINNET_CONTRACTS.filter(c => c.status === 'enforcing').length,                      // 1
  dormant: MAINNET_CONTRACTS.filter(c => ['dormant', 'standby'].includes(c.status)).length,       // 5
};

// Sepolia ACS Hook was the pre-mainnet bake (MARKETING_FACTS §1.10–§1.12).
// Mainnet promotion completed 2026-07-20 (see §1.13–§1.14).
// Sepolia addresses retained for historical proof trail (rotation drill + slash tests).
export const SEPOLIA_ACS = {
  chainId: 84532,
  mockKoji: '0xc11117EDEBf959Dd09CaDF0FC60701C5A32fF9Ce',
  hook:     '0x5662668FB18bc504e05f4809005f8caa104103BA',
  oracle:   '0x2Ac226061aE5beFe61581140362129183d7a939e',
  mainnetTarget: 'live on Base mainnet (2026-07-20)',
};

// Off-chain truth (MARKETING_FACTS §2).
export const OFF_CHAIN_SERVICES = {
  knowledgeGraph: { storage: 'MongoDB', exposedAt: '/api/agent-credit/knowledge/*', pinning: 'roadmap (synthetic CIDs today)' },
  formalVerification: { cvlDrafts: 4, certoraRunsCompleted: 0, target: 'Q3 2026 (paid engagement)' },
};

// Mainnet WithdrawalGuard for the live viem read in the widget.
export const MAINNET = {
  chainId: 8453,
  rpcUrl: 'https://mainnet.base.org',
  withdrawalGuard: '0xE3D4B3BA88f53C2D2a499cb46Cd4679dE6c2021E',
  explorer: 'https://basescan.org',
};
